import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { LearningTag, LearningTagResponse } from '@/types';

const OPTIONS: Array<{
  tag: LearningTag;
  labelKey: 'learningOptionDefault' | 'learningOptionSkip' | 'learningOptionForceLearn';
  titleKey: 'learningOptionDefaultTitle' | 'learningOptionSkipTitle' | 'learningOptionForceLearnTitle';
}> = [
  { tag: 'default', labelKey: 'learningOptionDefault', titleKey: 'learningOptionDefaultTitle' },
  { tag: 'skip', labelKey: 'learningOptionSkip', titleKey: 'learningOptionSkipTitle' },
  { tag: 'force-learn', labelKey: 'learningOptionForceLearn', titleKey: 'learningOptionForceLearnTitle' },
];

interface CloseConversationDialogProps {
  sessionId: string;
  /** True while the learning choice is being saved: buttons and Escape are inert so nothing is sent twice. */
  busy: boolean;
  /** The last attempt's failure notice (the learning choice could not be saved, so nothing was closed). */
  error: string | null;
  /** `chosen` is the selected learning disposition, `saved` the one the server held when the dialog opened. */
  onConfirm: (chosen: LearningTag, saved: LearningTag) => void;
  onCancel: () => void;
}

/**
 * The confirm for "Close conversation", carrying the "teach the bot from this chat" choice (operator, 2026-10-12: the
 * choice lives here, not under the chat). It loads the SAVED disposition (GET learning-tag; a failed read keeps
 * 'default', the server's own fallback for an untagged session) and hands both the chosen and the saved value to the
 * caller, which saves a changed choice BEFORE closing - learning reads it when the conversation closes.
 *
 * Built into the page, never `window.confirm` (the host blocks native dialogs). Default focus is Cancel; nothing is sent
 * until Close is pressed; Tab stays inside. Logical properties only, so it follows the root `dir`.
 */
export const CloseConversationDialog: React.FC<CloseConversationDialogProps> = ({
  sessionId,
  busy,
  error,
  onConfirm,
  onCancel,
}) => {
  const { t } = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [saved, setSaved] = useState<LearningTag>('default');
  const [chosen, setChosen] = useState<LearningTag>('default');

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    axiosClient
      .get<LearningTagResponse>(API.LEARNING.GET_TAG(sessionId))
      .then((resp) => {
        const tag = resp?.data?.tag;
        if (!cancelled && tag) {
          setSaved(tag);
          setChosen(tag);
        }
      })
      .catch(() => {
        /* keep 'default' - see above */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (!busy) onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="ac-modal-backdrop" data-testid="close-backdrop">
      <div
        ref={dialogRef}
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-conv-title"
        aria-describedby="close-conv-body"
        data-testid="close-dialog"
        onKeyDown={onKeyDown}
      >
        <h2 id="close-conv-title" className="ac-modal-title">
          {t('closeConversation')}
        </h2>
        <p id="close-conv-body" className="ac-modal-body">
          {t('closeDialogBody')}
        </p>

        <div className="close-learn" data-testid="learning-tag-bar">
          <span className="close-learn-label">
            <Icon name="sparkle" size={13} style={{ color: 'var(--consult)' }} />
            {t('learningHeader')}
          </span>
          <div className="ac-seg" role="group" aria-label={t('learningTagAriaLabel')}>
            {OPTIONS.map((o) => (
              <button
                key={o.tag}
                type="button"
                className={chosen === o.tag ? 'active' : ''}
                aria-pressed={chosen === o.tag}
                title={t(o.titleKey)}
                disabled={busy}
                onClick={() => setChosen(o.tag)}
                data-testid={`learning-tag-${o.tag}`}
              >
                {t(o.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div role="alert" className="ac-modal-error" data-testid="close-error">
            {error}
          </div>
        )}

        <div className="ac-modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="ac-btn ghost sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="close-cancel"
          >
            {t('closeCancel')}
          </button>
          <button
            type="button"
            className="ac-btn primary sm"
            onClick={() => onConfirm(chosen, saved)}
            disabled={busy}
            data-testid="close-confirm"
          >
            {busy ? t('closeClosing') : t('closeConversation')}
          </button>
        </div>
      </div>
    </div>
  );
};
